import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Autocomplete, TextField, Typography, Alert } from '@mui/material';
import { bloomberg } from '../theme/bloombergTheme';
import { getModelPortfolios, getAvailableBenchmarks, setYardstick, Scorecard } from '../services/api';

/**
 * Choose what a portfolio is judged against. Theme models come first: built
 * the same way (equal weight, local-currency prices), they make the fairest
 * benchmark when one matches. Otherwise an ETF from the benchmark list —
 * preferably one listed in the portfolio's own currency.
 */

type Option = { ref: string; label: string; group: string };

export default function YardstickPicker({ open, portfolioId, portfolioName, current, onClose, onSaved }: {
  open: boolean;
  portfolioId: number;
  portfolioName: string;
  current: string | null;
  onClose: () => void;
  onSaved: (s: Scorecard) => void;
}) {
  const [options, setOptions] = useState<Option[]>([]);
  const [value, setValue] = useState<Option | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || options.length) return;
    Promise.all([getModelPortfolios(), getAvailableBenchmarks()]).then(([models, etfs]) => {
      const themeModels = models.filter((m) => m.kind === 'theme' && m.id !== portfolioId)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((m) => ({ ref: `model:${m.id}`, label: m.name, group: 'Theme models (same method)' }));
      const funds = etfs.filter((b) => !/short|inverse/i.test(b.category))
        .map((b) => ({ ref: b.symbol, label: `${b.symbol} · ${b.name}`, group: b.category }));
      setOptions([...themeModels, ...funds]);
    }).catch(() => setError('Could not load the choices.'));
  }, [open, options.length, portfolioId]);

  const initial = useMemo(() => options.find((o) => o.ref === current) || null, [options, current]);
  useEffect(() => { if (open) { setValue(initial); setError(null); } }, [open, initial]);

  const save = async () => {
    setSaving(true); setError(null);
    try {
      onSaved(await setYardstick(portfolioId, value?.ref ?? null));
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Could not save the benchmark.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>Benchmark for {portfolioName}</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: bloomberg.text.secondary, mb: 2 }}>
          What this portfolio should beat. A matching theme model is the fairest comparison; otherwise pick an ETF,
          ideally listed in the portfolio's own currency.
        </Typography>
        <Autocomplete
          options={options}
          groupBy={(o) => o.group}
          getOptionLabel={(o) => o.label}
          isOptionEqualToValue={(a, b) => a.ref === b.ref}
          value={value}
          onChange={(_, v) => setValue(v)}
          loading={!options.length}
          renderInput={(params) => <TextField {...params} label="Judge against" size="small" autoFocus />}
        />
        {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving || (value?.ref ?? null) === current}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
